import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

export const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/the-game", label: "The Game" },
  { to: "/the-players", label: "The Players" },
  { to: "/house-rules", label: "House Rules" },
  { to: "/first-game", label: "First Game?" },
  { to: "/lounges", label: "Upcoming Lounges" },
];

const SUITS = ["♠", "♥", "♦", "♣", "♠", "♥"];

const Wordmark = () => (
  <Link to="/" className="flex items-center gap-3" data-testid="header-wordmark-link">
    <img
      src="/assets/players-lounge-logo.png"
      alt="Players Lounge monogram"
      className="h-9 w-9 object-contain"
    />
    <span className="font-serif text-lg tracking-wide text-ivory">
      Players <span className="italic text-brass">Lounge</span>
    </span>
  </Link>
);

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-brass/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
          <Wordmark />
          <nav aria-label="Primary" className="hidden items-center gap-6 lg:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    isActive ? "text-brass" : "text-ivory/70 hover:text-ivory"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/deal-me-in"
              data-testid="header-deal-me-in-button"
              className="hidden h-11 items-center rounded-xl bg-burgundy px-5 text-sm font-semibold tracking-wide text-ivory transition-all duration-200 hover:bg-[#a33847] hover:shadow-[0_0_24px_rgba(140,46,59,0.45)] sm:inline-flex"
            >
              Deal Me In
            </Link>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="header-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-brass/25 text-ivory transition-colors hover:border-brass/60 lg:hidden"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-ink lg:hidden"
            data-testid="mobile-nav-drawer"
          >
            <div className="lamp-glow flex h-full flex-col justify-between px-6 pb-10 pt-24">
              <nav aria-label="Mobile" className="flex flex-col gap-1">
                {NAV_ITEMS.map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06, duration: 0.45 }}
                  >
                    <NavLink
                      to={item.to}
                      data-testid={`drawer-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={({ isActive }) =>
                        `group flex items-baseline gap-4 border-b border-brass/10 py-4 ${
                          isActive ? "text-brass" : "text-ivory"
                        }`
                      }
                    >
                      <span aria-hidden="true" className="text-sm text-brass/60">
                        {SUITS[i]}
                      </span>
                      <span className="font-serif text-3xl transition-transform duration-200 group-active:translate-x-1">
                        {item.label}
                      </span>
                    </NavLink>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.45 }}
                >
                  <NavLink
                    to="/deal-me-in"
                    data-testid="drawer-deal-me-in-link"
                    className="group flex items-baseline gap-4 border-b border-brass/10 py-4 text-burgundy"
                  >
                    <span aria-hidden="true" className="text-sm text-brass/60">
                      ♦
                    </span>
                    <span className="font-serif text-3xl italic text-brass">Deal Me In</span>
                  </NavLink>
                </motion.div>
              </nav>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-xs tracking-[0.2em] text-ivory/50"
              >
                Sydney · 18+ · Private events · Discretion respected
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SiteHeader;
